import { useState } from 'react'
import { useParams } from 'react-router'
import LoadingSpinner from '../icons/LoadingSpinner.tsx'
import {
  useClubAdminCreateAddressMutation,
  useClubAdminDeleteAddressMutation,
  useClubAdminListAddressesQuery,
  useClubAdminUpdateAddressMutation,
} from '../../features/SpeechAgendaApiSlice.ts'
import { useAppAuth } from '../../app/hooks/UseAppAuth.ts'
import { Address } from '../../generated'
import { getApiErrorCode, getApiErrorMessage } from '../../utils/ApiError.ts'
import AddressTable from './AddressTable.tsx'
import AddressFormDialog, {
  type AddressFormState,
} from '../modals/AddressFormDialog.tsx'
import SimpleModalDialog from '../modals/SimpleModalDialog.tsx'
import ConfirmActionDialog from '../modals/ConfirmActionDialog.tsx'

interface AddressManagementHeaderProps {
  total: number
  onAddAddress: () => void
}

const AddressManagementHeader = ({
  total,
  onAddAddress,
}: AddressManagementHeaderProps) => (
  <header className="mb-6 py-2 flex items-center justify-between gap-3">
    <h3 className="font-semibold text-gray-900">Addresses ({total})</h3>

    <button
      type="button"
      onClick={onAddAddress}
      className="inline-flex items-center justify-center gap-x-2 rounded-md bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500">
      Add address
    </button>
  </header>
)

const emptyForm: AddressFormState = {
  name: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zip: '',
}

const toFormState = (address: Address): AddressFormState => ({
  name: address.name,
  addressLine1: address.addressLine1,
  addressLine2: address.addressLine2 ?? '',
  city: address.city,
  state: address.state,
  zip: address.zip,
})

// Mirror the server's image rules for fast feedback (the server enforces them
// too and returns 413/4xx otherwise).
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const MAX_IMAGE_BYTES = 5 * 1024 * 1024

const validateImageFile = (file: File): string | null => {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return 'Image must be a JPEG, PNG, or WebP file'
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return 'Image must be 5 MB or smaller'
  }
  return null
}

const AddressManagement = () => {
  const { clubId = '' } = useParams()
  const { userLoaded } = useAppAuth()

  const {
    data: addresses,
    isLoading,
    isError,
    isSuccess,
  } = useClubAdminListAddressesQuery(clubId, {
    skip: !clubId || !userLoaded,
  })

  const [createAddress, { isLoading: isCreating }] =
    useClubAdminCreateAddressMutation()
  const [updateAddress, { isLoading: isUpdating }] =
    useClubAdminUpdateAddressMutation()
  const [deleteAddress] = useClubAdminDeleteAddressMutation()

  const [modalState, setModalState] = useState({
    isOpen: false,
    success: true,
    title: '',
    description: '',
    buttonText: 'Close',
  })

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Address | null>(null)
  const [formState, setFormState] = useState<AddressFormState>(emptyForm)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [removeImage, setRemoveImage] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    addressId: string
    name: string
  } | null>(null)

  const openModal = ({
    success,
    title,
    description,
    buttonText,
  }: {
    success: boolean
    title: string
    description: string
    buttonText?: string
  }) => {
    setModalState({
      isOpen: true,
      success,
      title,
      description,
      buttonText: buttonText ?? 'Close',
    })
  }

  const closeModal = () => {
    setModalState((previous) => ({ ...previous, isOpen: false }))
  }

  const handleAddAddress = () => {
    setEditing(null)
    setFormState(emptyForm)
    setSelectedFile(null)
    setRemoveImage(false)
    setFormError(null)
    setIsDialogOpen(true)
  }

  const handleEditAddress = (address: Address) => {
    setEditing(address)
    setFormState(toFormState(address))
    setSelectedFile(null)
    setRemoveImage(false)
    setFormError(null)
    setIsDialogOpen(true)
  }

  const closeDialog = () => {
    setIsDialogOpen(false)
    setFormError(null)
  }

  const handleFieldChange = (field: keyof AddressFormState, value: string) => {
    setFormState((previous) => ({ ...previous, [field]: value }))
  }

  const handleFileChange = (file: File | null) => {
    if (file) {
      const validationError = validateImageFile(file)
      if (validationError) {
        setFormError(validationError)
        setSelectedFile(null)
        return
      }
      // Picking a new image supersedes a pending removal.
      setRemoveImage(false)
    }
    setFormError(null)
    setSelectedFile(file)
  }

  const submitAddress = async () => {
    const name = formState.name.trim()
    const addressLine1 = formState.addressLine1.trim()
    const city = formState.city.trim()
    const state = formState.state.trim()
    const zip = formState.zip.trim()

    if (!name || !addressLine1 || !city || !state || !zip) {
      setFormError('Name, address line 1, city, state, and ZIP are required')
      return
    }

    if (!clubId) {
      setFormError(
        'Club context is missing. Please reload the page and try again'
      )
      return
    }

    const addressLine2 = formState.addressLine2.trim()

    const body = {
      name,
      addressLine1,
      addressLine2: addressLine2 || undefined,
      city,
      state,
      zip,
    }

    setFormError(null)

    try {
      if (editing) {
        await updateAddress({
          clubId,
          addressId: String(editing.id),
          // A selected file replaces the image, so removeImage is irrelevant
          // there; only honor it when no new file is being uploaded.
          body: { ...body, removeImage: removeImage && !selectedFile },
          file: selectedFile ?? undefined,
        }).unwrap()
      } else {
        await createAddress({
          clubId,
          body,
          file: selectedFile ?? undefined,
        }).unwrap()
      }

      setIsDialogOpen(false)
      openModal({
        success: true,
        title: 'Address saved',
        description: `${name} has been saved to your club address book.`,
      })
    } catch (error) {
      console.error('Failed to save club address', error)
      setFormError(getApiErrorMessage(error, 'Unable to save address. Try again later'))
    }
  }

  const handleRemoveAddress = (payload: {
    addressId: string
    name: string
  }) => {
    setDeleteConfirmation(payload)
  }

  const confirmRemoveAddress = async () => {
    if (!deleteConfirmation || !clubId) {
      setDeleteConfirmation(null)
      return
    }

    const target = deleteConfirmation
    setDeleteConfirmation(null)

    try {
      await deleteAddress({
        clubId,
        addressId: target.addressId,
      }).unwrap()

      openModal({
        success: true,
        title: 'Address removed',
        description: `${target.name} was removed successfully.`,
      })
    } catch (error) {
      console.error('Failed to remove club address', error)

      if (getApiErrorCode(error) === 'ADDRESS_IN_USE') {
        openModal({
          success: false,
          title: 'Address in use',
          description:
            'This address is used by a meeting. Update or remove that meeting first, then delete the address.',
        })
        return
      }

      openModal({
        success: false,
        title: 'Removal failed',
        description: getApiErrorMessage(
          error,
          `Unable to remove ${target.name}. Try again later`
        ),
      })
    }
  }

  if (isLoading) {
    return (
      <div className="py-6 flex justify-center">
        <LoadingSpinner message="Loading addresses..." />
      </div>
    )
  }

  if (isError) {
    return (
      <p className="text-red-600">Unable to load club addresses right now</p>
    )
  }

  return (
    <div className="py-4 border-b">
      <AddressManagementHeader
        total={addresses?.length ?? 0}
        onAddAddress={handleAddAddress}
      />

      {isSuccess && addresses && addresses.length > 0 && (
        <div className="overflow-x-auto">
          <AddressTable
            addresses={addresses}
            onEdit={handleEditAddress}
            onRemove={handleRemoveAddress}
          />
        </div>
      )}

      {isSuccess && (!addresses || addresses.length === 0) && (
        <p className="text-sm text-gray-600">
          No addresses yet. Add one so you can attach it to a meeting.
        </p>
      )}

      <AddressFormDialog
        isOpen={isDialogOpen}
        mode={editing ? 'edit' : 'create'}
        onClose={closeDialog}
        formState={formState}
        onFieldChange={handleFieldChange}
        selectedFile={selectedFile}
        onFileChange={handleFileChange}
        existingLogoUrl={editing?.logoUrl ?? null}
        removeImage={removeImage}
        onRemoveImageChange={setRemoveImage}
        onSubmit={submitAddress}
        isSubmitting={isCreating || isUpdating}
        error={formError}
      />

      <SimpleModalDialog
        isOpen={modalState.isOpen}
        onClose={closeModal}
        success={modalState.success}
        title={modalState.title}
        description={modalState.description}
        buttonText={modalState.buttonText}
      />

      <ConfirmActionDialog
        isOpen={Boolean(deleteConfirmation)}
        title="Remove address?"
        description={
          deleteConfirmation
            ? `Are you sure you want to remove ${deleteConfirmation.name} from this club?`
            : ''
        }
        confirmText="Remove"
        cancelText="Cancel"
        onConfirm={confirmRemoveAddress}
        onCancel={() => setDeleteConfirmation(null)}
      />
    </div>
  )
}

export default AddressManagement
